import React, { PureComponent } from 'react';
import { Flex } from 'app/components/common/Flex';
import { LevelScale, Range } from 'app/common/graph/Scale';
import { FlightLevel, FlightLevel_Unit, FlightLevelBand } from 'app/model/CORE';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import Utils from 'app/common/util/Utils';
import ConditionalRender from 'app/common/components/ConditionalRender';

type ClickStatus = undefined | 'MIN' | 'MAX';
const SLIDE_STEP_FL = 5;

interface FlightLevelSelectorProps {
    label?: string;
    length: string; //note that doesn't work with %
    flRange: FlightLevelBand;
    onFlRangeChange: (FlightLevelBand) => void;
    markIntervalFL: number;
    orientation?: 'vertical' | 'horizontal';
    highlights?: Array<FlightLevelBand>;
}

export class FlightLevelSelector extends PureComponent<FlightLevelSelectorProps> {
    clicked: ClickStatus = undefined;
    svgRef: React.RefObject<SVGSVGElement> = React.createRef();
    scale: LevelScale;
    isHorizontalOrientation: boolean;

    constructor(props) {
        super(props);
        const flRange = {
            min: { value: 0, unit: FlightLevel_Unit.FLIGHT_LEVEL },
            max: { value: 999, unit: FlightLevel_Unit.FLIGHT_LEVEL },
        };
        this.isHorizontalOrientation =
            'horizontal' === (Utils.isExists(props.orientation) ? props.orientation : 'horizontal');
        this.scale = this.isHorizontalOrientation
            ? LevelScale.newLevelScale(new Range(0, 100), flRange)
            : LevelScale.newLevelScale(new Range(100, 0), flRange);
    }

    componentDidUpdate(prevProps, prevState): void {}

    renderMarks = () => {
        if (!this.scale) return undefined;

        let level: number = FlightLevelUtils.getLevelValue(this.scale.toFlightLevel(this.scale.getScale().target.from));
        const marks = [];
        while (FlightLevelUtils.getLevelValue(this.scale.toFlightLevel(this.scale.getScale().target.to)) - level > 0) {
            const flightLevel = { value: level, unit: FlightLevel_Unit.FLIGHT_LEVEL };
            const coordinate = this.scale.toSource(flightLevel);
            const color = FlightLevelUtils.contains(this.props.flRange, flightLevel) ? '#FFFF00' : '#FFFF0030';
            if (this.isHorizontalOrientation)
                marks.push(
                    <line
                        key={`mark-${level}`}
                        x1={coordinate + '%'}
                        y1="25%"
                        x2={coordinate + '%'}
                        y2="80%"
                        stroke={color}
                        strokeWidth="0.1em"
                        shapeRendering="geometricPrecision"
                    />,
                );
            else
                marks.push(
                    <line
                        key={`mark-${level}`}
                        y1={coordinate + '%'}
                        x1="25%"
                        y2={coordinate + '%'}
                        x2="80%"
                        stroke={color}
                        strokeWidth="0.1em"
                        shapeRendering="geometricPrecision"
                    />,
                );
            level += this.props.markIntervalFL;
        }
        return marks;
    };
    renderCursor = (level: FlightLevel) => {
        if (!this.scale || !level) return undefined;

        const coordinate = this.scale.toSource(level);
        if (this.isHorizontalOrientation) {
            return [
                <line
                    key="cursor-line"
                    x1={coordinate + '%'}
                    y1="0%"
                    x2={coordinate + '%'}
                    y2="100%"
                    stroke="#FFFF00"
                    strokeWidth="0.1em"
                    shapeRendering="geometricPrecision"
                />,
                <rect
                    key="cursor-rect"
                    x={coordinate.toFixed(5) + '%'}
                    y="0%"
                    width={'0.3em'}
                    height={'0.3em'}
                    fill={'#FFFF00'}
                    style={{ transform: 'translateX(-0.15em)' }}
                />,
            ];
        } else {
            return [
                <line
                    key="cursor-line"
                    y1={coordinate + '%'}
                    x1="0%"
                    y2={coordinate + '%'}
                    x2="100%"
                    stroke="#FFFF00"
                    strokeWidth="0.1em"
                    shapeRendering="geometricPrecision"
                />,
                <rect
                    key="cursor-rect"
                    y={coordinate.toFixed(5) + '%'}
                    x="0%"
                    width={'0.3em'}
                    height={'0.3em'}
                    fill={'#FFFF00'}
                    style={{ transform: 'translateY(-0.15em)' }}
                />,
            ];
        }
    };

    renderHighlights = () => {
        if (!this.scale || !this.props.highlights) return undefined;

        const elements = [];
        let index = 0;
        for (const highlightFlightLevelBand of this.props.highlights) {
            const coordinateStart = this.scale.toSource(highlightFlightLevelBand.min);
            const coordinateEnd = this.scale.toSource(highlightFlightLevelBand.max);
            if (this.isHorizontalOrientation) {
                elements.push(
                    <line
                        key={`highlight-${index++}`}
                        y1={'90%'}
                        x1={coordinateStart + '%'}
                        y2={'90%'}
                        x2={coordinateEnd + '%'}
                        stroke="red"
                        strokeWidth="0.4em"
                        shapeRendering="geometricPrecision"
                    />,
                );
            } else {
                elements.push(
                    <line
                        key={`highlight-${index++}`}
                        x1={'90%'}
                        y1={coordinateStart + '%'}
                        x2={'90%'}
                        y2={coordinateEnd + '%'}
                        stroke="red"
                        strokeWidth="0.4em"
                        shapeRendering="geometricPrecision"
                    />,
                );
            }
        }
        return elements;
    };
    handleMouseDown = e => {
        // If the click is before the mean between current MAX and MIN,
        // consider that MIN will change with mouse move
        const rect = this.svgRef.current.getBoundingClientRect();
        let coordinate;
        let clickedLevelValue;
        if (this.isHorizontalOrientation) {
            coordinate = e.clientX - rect.left;
            clickedLevelValue = FlightLevelUtils.getLevelValue(this.scale.toTarget((coordinate * 100) / rect.width));
        } else {
            coordinate = e.clientY - rect.top;
            clickedLevelValue = FlightLevelUtils.getLevelValue(this.scale.toTarget((coordinate * 100) / rect.height));
        }

        const minFL = FlightLevelUtils.getLevelValue(this.props.flRange.min);
        const maxFL = FlightLevelUtils.getLevelValue(this.props.flRange.max);
        if (2 * clickedLevelValue < minFL + maxFL) {
            this.clicked = 'MIN';
        } else {
            this.clicked = 'MAX';
        }

        this.handleMouseMove(e);
    };
    handleMouseUp = e => {
        this.clicked = undefined;
    };
    handleMouseMove = e => {
        if (!this.clicked || !this.scale) return undefined;

        const rect = this.svgRef.current.getBoundingClientRect();
        let coordinate;
        if (this.isHorizontalOrientation) {
            coordinate = e.clientX - rect.left;
        } else {
            coordinate = e.clientY - rect.top;
        }
        if (coordinate > 0) {
            let newLevelValue;
            if (this.isHorizontalOrientation) {
                newLevelValue = FlightLevelUtils.getLevelValue(this.scale.toTarget((coordinate * 100) / rect.width));
            } else {
                newLevelValue = FlightLevelUtils.getLevelValue(this.scale.toTarget((coordinate * 100) / rect.height));
            }
            newLevelValue = Math.floor(newLevelValue / SLIDE_STEP_FL) * SLIDE_STEP_FL; // Only allow levels that are multiple of 5
            const newFL: FlightLevel = { value: newLevelValue, unit: FlightLevel_Unit.FLIGHT_LEVEL };
            if (this.clicked === 'MIN' && newLevelValue !== this.props.flRange?.min) {
                if (newLevelValue > FlightLevelUtils.getLevelValue(this.props.flRange.max)) {
                    return;
                }
                this.props.onFlRangeChange({ min: newFL, max: this.props.flRange?.max });
            } else if (this.clicked === 'MAX' && newLevelValue !== this.props.flRange?.max) {
                if (newLevelValue < FlightLevelUtils.getLevelValue(this.props.flRange.min)) {
                    return;
                }
                this.props.onFlRangeChange({ min: this.props.flRange?.min, max: newFL });
            }
        }
    };
    handleMouseOut = e => {
        this.clicked = undefined;
    };
    renderLabels = () => {
        if (!this.scale || !this.props?.flRange) return undefined;
        if (this.isHorizontalOrientation) {
            return (
                <Flex size="20%" style={{ position: 'relative', alignItems: 'flex-end' }}>
                    <span
                        style={{
                            position: 'absolute',
                            left: `${this.scale?.toSource(this.props?.flRange?.min) || 0}%`,
                            transform: 'translateX(-50%)',
                            fontWeight: 'bold',
                            fontSize: '0.85em',
                            color: 'white',
                        }}>
                        {FlightLevelUtils.getLevelValue(this.props?.flRange?.min)}
                    </span>
                    <span
                        style={{
                            position: 'absolute',
                            left: `${this.scale?.toSource(this.props?.flRange?.max) || 0}%`,
                            transform: 'translateX(-50%)',
                            fontWeight: 'bold',
                            fontSize: '0.85em',
                            color: 'white',
                        }}>
                        {FlightLevelUtils.getLevelValue(this.props?.flRange?.max)}
                    </span>
                </Flex>
            );
        } else {
            return (
                <Flex size="20%" style={{ position: 'relative', justifyContent: 'flex-end' }}>
                    <span
                        style={{
                            position: 'absolute',
                            top: `${this.scale?.toSource(this.props?.flRange?.min) || 0}%`,
                            transform: 'translateY(-50%)',
                            fontWeight: 'bold',
                            fontSize: '0.85em',
                            color: 'white',
                        }}>
                        {FlightLevelUtils.getLevelValue(this.props?.flRange?.min)}
                    </span>
                    <span
                        style={{
                            position: 'absolute',
                            top: `${this.scale?.toSource(this.props?.flRange?.max) || 0}%`,
                            transform: 'translateY(-50%)',
                            fontWeight: 'bold',
                            fontSize: '0.85em',
                            color: 'white',
                        }}>
                        {FlightLevelUtils.getLevelValue(this.props?.flRange?.max)}
                    </span>
                </Flex>
            );
        }
    };
    render() {
        let labelStyle = Object.assign({}, { fontWeight: 'bold', alignSelf: 'center' });
        if (!this.isHorizontalOrientation) {
            labelStyle = Object.assign({}, labelStyle, { marginBottom: '20px', textAlign: 'center' });
        }
        return (
            <Flex
                direction={Utils.isExists(this.props.orientation) ? this.props.orientation : 'horizontal'}
                style={{ alignItems: 'center' }}>
                <ConditionalRender condition={Utils.isExists(this.props.label)}>
                    <Flex direction="vertical" style={{ flex: '0 0 auto', margin: '0 1em' }}>
                        <ConditionalRender condition={this.isHorizontalOrientation}>
                            <Flex size="20%" />
                        </ConditionalRender>
                        <Flex>
                            <span style={labelStyle}>{this.props.label}</span>
                        </Flex>
                    </Flex>
                </ConditionalRender>
                <Flex direction={this.isHorizontalOrientation ? 'vertical' : 'horizontal'} size={this.props.length}>
                    {this.renderLabels()}

                    <Flex style={{ position: 'relative' }}>
                        <span
                            style={{ position: 'absolute', width: '100%', height: '100%' }}
                            onMouseDown={this.handleMouseDown}
                            onMouseMove={this.handleMouseMove}
                            onMouseUp={this.handleMouseUp}
                            onMouseOut={this.handleMouseOut}
                        />
                        <svg
                            ref={this.svgRef}
                            style={{
                                alignSelf: 'center',
                                height: this.isHorizontalOrientation ? '2.5em' : this.props.length,
                                width: this.isHorizontalOrientation ? this.props.length : '2.5em',
                                shapeRendering: 'crispEdges',
                            }}>
                            {this.renderHighlights()}
                            {this.renderMarks()}
                            {this.renderCursor(this.props.flRange.min)}
                            {this.renderCursor(this.props.flRange.max)}
                        </svg>
                    </Flex>
                </Flex>
            </Flex>
        );
    }
}
